import type { DraftField, DraftSection, DraftValidationIssue, LogicRule } from "@sga/shared";

/**
 * The Logic tab: "a rules list view (not just per-field toggles) so an admin can audit a complex
 * form's behaviour at a glance" (docs/01-PRODUCT-SPEC.md §3.3), rendered as readable sentences —
 * "When *State of Origin* is one of *Delta, Edo*, show section *Host Community Details*".
 */

const OPERATOR_PHRASES: Record<string, string> = {
  equals: "is",
  not_equals: "is not",
  contains: "contains",
  greater_than: "is greater than",
  less_than: "is less than",
  is_empty: "is empty",
  is_not_empty: "is answered",
  is_one_of: "is one of",
};

const ACTION_PHRASES: Record<string, string> = {
  show: "show",
  hide: "hide",
  require: "make required",
  unrequire: "make optional",
  set_value: "set the value of",
  jump_to_page: "jump to page",
  skip_page: "skip page",
};

const Emphasis = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <em className="font-medium not-italic text-primary">{children}</em>
);

export const ruleToSentence = (
  rule: LogicRule,
  fields: DraftField[],
  sections: DraftSection[],
): JSX.Element => {
  const labelForField = (key: string): string => fields.find((f) => f.key === key)?.label ?? key;
  const labelForSection = (key: string): string =>
    sections.find((s) => s.key === key)?.title ?? key;

  const formatValue = (value: unknown): string =>
    Array.isArray(value) ? value.join(", ") : String(value ?? "");

  return (
    <span className="text-sm">
      When {rule.match === "any" ? <span className="text-muted-foreground">any of </span> : null}
      {rule.conditions.map((condition, index) => (
        <span key={index}>
          {index > 0 && (
            <span className="text-muted-foreground"> {rule.match === "any" ? "or" : "and"} </span>
          )}
          <Emphasis>{labelForField(condition.field)}</Emphasis>{" "}
          {OPERATOR_PHRASES[condition.operator] ?? condition.operator}
          {condition.value !== undefined &&
          condition.operator !== "is_empty" &&
          condition.operator !== "is_not_empty" ? (
            <>
              {" "}
              <Emphasis>{formatValue(condition.value)}</Emphasis>
            </>
          ) : null}
        </span>
      ))}
      {", "}
      {rule.effects.map((effect, index) => (
        <span key={index}>
          {index > 0 && <span className="text-muted-foreground"> and </span>}
          {ACTION_PHRASES[effect.action] ?? effect.action}{" "}
          {effect.targetType === "section"
            ? "section "
            : effect.targetType === "page"
              ? ""
              : "field "}
          <Emphasis>
            {effect.targetType === "section"
              ? labelForSection(effect.target)
              : effect.targetType === "page"
                ? effect.target
                : labelForField(effect.target)}
          </Emphasis>
        </span>
      ))}
      .
    </span>
  );
};

export const LogicTab = ({
  logic,
  fields,
  sections,
  issues,
  onChange,
}: {
  logic: LogicRule[];
  fields: DraftField[];
  sections: DraftSection[];
  issues: DraftValidationIssue[];
  onChange: (next: LogicRule[]) => void;
}): JSX.Element => {
  const addRule = (): void => {
    const firstField = fields[0];
    if (!firstField) return;
    onChange([
      ...logic,
      {
        id: `rule-${Date.now()}`,
        match: "all",
        conditions: [{ field: firstField.key, operator: "is_not_empty" }],
        effects: [
          { action: "show", targetType: "field", target: fields[1]?.key ?? firstField.key },
        ],
      },
    ]);
  };

  const updateRule = (index: number, next: LogicRule): void =>
    onChange(logic.map((rule, position) => (position === index ? next : rule)));

  return (
    <div className="space-y-3 p-4">
      {logic.length === 0 && (
        <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          No conditional logic yet. Rules let you show, hide or require fields based on earlier
          answers.
        </p>
      )}

      {logic.map((rule, index) => {
        // Inline warnings for problems the validate endpoint reported (Phase 4 prompt).
        const ruleIssues = issues.filter((issue) => issue.ruleId === rule.id);

        return (
          <div
            key={rule.id}
            className={`rounded-md border p-3 ${ruleIssues.length > 0 ? "border-brand-red" : ""}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">{ruleToSentence(rule, fields, sections)}</div>
              <button
                type="button"
                className="shrink-0 rounded border px-2 py-0.5 text-xs text-brand-red hover:bg-secondary"
                onClick={() => onChange(logic.filter((_, position) => position !== index))}
              >
                Remove
              </button>
            </div>

            {ruleIssues.map((issue, position) => (
              <p
                key={position}
                role="alert"
                className="mt-2 rounded bg-brand-red/10 p-2 text-xs text-brand-red"
              >
                {issue.message}
              </p>
            ))}

            <div className="mt-3 grid grid-cols-2 gap-2 border-t pt-3 text-xs">
              <label className="space-y-1">
                <span className="block text-[11px] uppercase text-muted-foreground">
                  When field
                </span>
                <select
                  className="w-full rounded border px-2 py-1"
                  value={rule.conditions[0]?.field ?? ""}
                  onChange={(event) =>
                    updateRule(index, {
                      ...rule,
                      conditions: [{ ...rule.conditions[0]!, field: event.target.value }],
                    })
                  }
                >
                  {fields.map((field) => (
                    <option key={field.key} value={field.key}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-1">
                <span className="block text-[11px] uppercase text-muted-foreground">Operator</span>
                <select
                  className="w-full rounded border px-2 py-1"
                  value={rule.conditions[0]?.operator ?? "is_not_empty"}
                  onChange={(event) =>
                    updateRule(index, {
                      ...rule,
                      conditions: [
                        {
                          ...rule.conditions[0]!,
                          operator: event.target
                            .value as LogicRule["conditions"][number]["operator"],
                        },
                      ],
                    })
                  }
                >
                  {Object.entries(OPERATOR_PHRASES).map(([operator, phrase]) => (
                    <option key={operator} value={operator}>
                      {phrase}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-1">
                <span className="block text-[11px] uppercase text-muted-foreground">Value</span>
                <input
                  className="w-full rounded border px-2 py-1"
                  value={String(rule.conditions[0]?.value ?? "")}
                  onChange={(event) =>
                    updateRule(index, {
                      ...rule,
                      conditions: [{ ...rule.conditions[0]!, value: event.target.value }],
                    })
                  }
                />
              </label>

              <label className="space-y-1">
                <span className="block text-[11px] uppercase text-muted-foreground">Then</span>
                <select
                  className="w-full rounded border px-2 py-1"
                  value={rule.effects[0]?.action ?? "show"}
                  onChange={(event) =>
                    updateRule(index, {
                      ...rule,
                      effects: [
                        {
                          ...rule.effects[0]!,
                          action: event.target.value as LogicRule["effects"][number]["action"],
                        },
                      ],
                    })
                  }
                >
                  {Object.entries(ACTION_PHRASES).map(([action, phrase]) => (
                    <option key={action} value={action}>
                      {phrase}
                    </option>
                  ))}
                </select>
              </label>

              <label className="col-span-2 space-y-1">
                <span className="block text-[11px] uppercase text-muted-foreground">Target</span>
                <select
                  className="w-full rounded border px-2 py-1"
                  value={`${rule.effects[0]?.targetType ?? "field"}:${rule.effects[0]?.target ?? ""}`}
                  onChange={(event) => {
                    const [targetType, target] = event.target.value.split(":");
                    updateRule(index, {
                      ...rule,
                      effects: [
                        {
                          ...rule.effects[0]!,
                          targetType: targetType as LogicRule["effects"][number]["targetType"],
                          target: target ?? "",
                        },
                      ],
                    });
                  }}
                >
                  <optgroup label="Fields">
                    {fields.map((field) => (
                      <option key={field.key} value={`field:${field.key}`}>
                        {field.label}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Sections">
                    {sections.map((section) => (
                      <option key={section.key} value={`section:${section.key}`}>
                        {section.title ?? section.key}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </label>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={addRule}
        disabled={fields.length === 0}
        className="w-full rounded-md border border-dashed py-2 text-xs hover:bg-secondary disabled:opacity-50"
      >
        Add rule
      </button>
    </div>
  );
};
